// ============================================
// LOYALTY DIALOG COMPONENT
// Le Cercle Aldrin's - Loyalty program management
// Extracted from POSPageV2.jsx
// ============================================

import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
    Gift, Phone, Loader2, Check, Edit, UserPlus, Minus, Plus 
} from 'lucide-react';
import { cn } from '../../lib/utils';

const LoyaltyDialog = ({
    open,
    onOpenChange,
    searchPhone,
    setSearchPhone,
    isSearching,
    member,
    getLoyaltyStatusColor,
    getLoyaltyStatusIcon,
    onSelectMember,
    onOpenPaymentDialog,
    cartLength,
    paymentDialogOpen,
    // Manual points mode (Admin only)
    canAddPointsManual,
    addPointsMode,
    setAddPointsMode,
    pointsToAdd,
    setPointsToAdd,
    pointsReason,
    setPointsReason,
    onManualPointsSubmit,
}) => {
    const handleClose = () => {
        onOpenChange(false);
    };

    const handleSelectMember = () => {
        onSelectMember(member);
        onOpenChange(false);
        if (!paymentDialogOpen && cartLength > 0) {
            onOpenPaymentDialog();
        }
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => {
            onOpenChange(isOpen);
            if (!isOpen) {
                setSearchPhone('');
                setAddPointsMode(false);
                setPointsToAdd(0);
                setPointsReason('');
            }
        }}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-green-800 dark:text-green-300">
                        <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                            <Gift className="w-5 h-5 text-white" />
                        </div>
                        Le Cercle Aldrin's
                    </DialogTitle>
                    <DialogDescription>
                        Programme de fidélité - Recherchez un client ou inscrivez-en un nouveau
                    </DialogDescription>
                </DialogHeader>
                
                <div className="py-4 space-y-4">
                    {/* Search by Phone */}
                    <div>
                        <label className="text-sm font-bold mb-2 block">Rechercher par téléphone</label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                type="tel"
                                placeholder="074 XX XX XX"
                                value={searchPhone}
                                onChange={(e) => setSearchPhone(e.target.value)}
                                className="pl-10"
                                data-testid="loyalty-search-phone"
                            />
                            {isSearching && (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                            )}
                        </div>
                    </div>
                    
                    {/* Member Found */}
                    {member && (
                        <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-200 dark:border-green-800 space-y-4">
                            {/* Member Info */}
                            <div className="flex items-center gap-4">
                                <div className={cn("w-14 h-14 rounded-full flex items-center justify-center", getLoyaltyStatusColor(member.status))}>
                                    {getLoyaltyStatusIcon(member.status)}
                                </div>
                                <div className="flex-1">
                                    <p className="font-bold text-lg">{member.first_name}</p>
                                    <div className="flex items-center gap-2">
                                        <Badge className={cn("text-xs", getLoyaltyStatusColor(member.status))}>
                                            {member.status}
                                        </Badge>
                                        <span className="text-sm text-muted-foreground">{member.phone}</span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-3xl font-black text-green-600">{member.points}</p>
                                    <p className="text-xs text-muted-foreground">points</p>
                                </div>
                            </div>
                            
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                                    <p className="text-xs text-muted-foreground">Total dépensé</p>
                                    <p className="font-bold text-sm">{(member.total_spent || 0).toLocaleString()} F</p>
                                </div>
                                <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                                    <p className="text-xs text-muted-foreground">Visites</p>
                                    <p className="font-bold text-sm">{member.visits || 0}</p>
                                </div>
                                <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                                    <p className="text-xs text-muted-foreground">Récompenses</p>
                                    <p className="font-bold text-sm">{member.rewards_redeemed?.length || 0}</p>
                                </div>
                            </div>
                            
                            {/* Next Reward Progress */}
                            {member.next_reward && (
                                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="text-muted-foreground">Prochaine récompense</span>
                                        <span className="text-green-600 font-bold">{member.next_reward.points_needed} pts restants</span>
                                    </div>
                                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-green-500 rounded-full transition-all"
                                            style={{ width: `${(member.points / member.next_reward.points_required) * 100}%` }}
                                        />
                                    </div>
                                    <p className="text-xs text-center mt-1 text-muted-foreground">{member.next_reward.name}</p>
                                </div>
                            )}
                            
                            {/* Actions */}
                            {!addPointsMode ? (
                                <div className="flex gap-2">
                                    <Button
                                        className="flex-1 bg-green-600 hover:bg-green-700"
                                        onClick={handleSelectMember}
                                        data-testid="select-loyalty-member-btn"
                                    >
                                        <Check className="w-4 h-4 mr-2" />
                                        Sélectionner
                                    </Button>
                                    {/* Admin only: Manual points adjustment */}
                                    {canAddPointsManual && (
                                        <Button
                                            variant="outline"
                                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                            onClick={() => setAddPointsMode(true)}
                                        >
                                            <Edit className="w-4 h-4 mr-2" />
                                            Points
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-center gap-4">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setPointsToAdd(p => p - 1)}
                                            className="h-12 w-12"
                                        >
                                            <Minus className="w-5 h-5" />
                                        </Button>
                                        <Input
                                            type="number"
                                            value={pointsToAdd}
                                            onChange={(e) => setPointsToAdd(parseInt(e.target.value) || 0)}
                                            className="w-24 text-center text-xl font-bold h-12"
                                        />
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setPointsToAdd(p => p + 1)}
                                            className="h-12 w-12"
                                        >
                                            <Plus className="w-5 h-5" />
                                        </Button>
                                    </div>
                                    <Input
                                        placeholder="Raison (obligatoire)"
                                        value={pointsReason}
                                        onChange={(e) => setPointsReason(e.target.value)}
                                    />
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            className="flex-1"
                                            onClick={() => {
                                                setAddPointsMode(false);
                                                setPointsToAdd(0);
                                                setPointsReason('');
                                            }}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                                            onClick={onManualPointsSubmit}
                                            disabled={pointsToAdd === 0 || !pointsReason}
                                        >
                                            Appliquer {pointsToAdd > 0 ? '+' : ''}{pointsToAdd}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Not Found Message */}
                    {searchPhone.length >= 3 && !isSearching && !member && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-xl text-center">
                            <p className="text-muted-foreground mb-3">Aucun membre trouvé avec ce numéro</p>
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => window.open('/client/fidelite', '_blank')}
                            >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Inscrire un nouveau membre
                            </Button>
                        </div>
                    )}
                    
                    {/* Info */}
                    <div className="text-center text-xs text-muted-foreground">
                        <p>1000 FCFA = 1 point fidélité</p>
                        <p className="mt-1">10 pts = Café offert • 20 pts = Dessert • 40 pts = -2000F • 60 pts = Plat du jour</p>
                    </div>
                </div>
                
                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Fermer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default LoyaltyDialog;
